/*───────────────────────────────────────────────────────────────────────────*\
  File        : app/pages/support/SupportPage.tsx
  Description : Client-component providing FAQ accordion and “Contact
                Support” form. Submits JSON payload to
                `/api/support/contact` and shows confirmation on success.
\*───────────────────────────────────────────────────────────────────────────*/

"use client";

import type React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, Clock, CheckCircle } from "lucide-react";

/*─────────────────────────────  Data  ────────────────────────────────────*/

const faqs = [
  {
    question: "How do I get started with Roomvia?",
    answer:
      "Getting started is easy! Simply sign up for an account, then head to our Designer tool. You can either upload a photo of your existing room or start from scratch by selecting your room type, size, and style preferences. Our AI will generate beautiful design options for you in minutes.",
  },
  {
    question: "What are design credits and how do they work?",
    answer:
      "Design credits are the currency used whenever you perform an AI action in the app. Different actions may use different amounts of credits — for example, generating new room designs from your photo will cost more credits than generating designs from scratch. To keep things transparent, each action button clearly shows the number of credits it will use before you proceed. Monthly subscribers get 250 credits per month and we also offer pay-as-you-go credit packs.",
  },
  {
    question: "Can I upload my own room photos?",
    answer:
      "Yes! You can upload photos of your existing rooms and our AI will create a blank canvas, preserving the room's structure and fixed elements. From there, you can apply a variety of design styles and color options that bring you ideas to life.",
  },
  {
    question: "What file formats are supported for room photos?",
    answer:
      'We support common image formats including JPG, PNG, and HEIC. For best results, use high-resolution, landscape photos taken in good lighting with the entire room visible. Photos with less visible clutter tend to produce better results, so consider a quick "tidy up" before taking your photo.',
  },
  {
    question: "How accurate are the design recommendations?",
    answer:
      "Our AI is trained on thousands of professional interior designs and takes into account factors like room dimensions, style preferences, and color schemes. That said, AI isn't perfect and will sometimes make mistakes. The goal of the app is to inspire design thinking and make interior design more accessible, not to produce a completely accurate rendering of your room. Results may vary based on your room's layout and original photo quality.",
  },
  {
    question: "Can I purchase the recommended products?",
    answer:
      "Not directly. When you create a project from a design image, our AI identifies and describes the prominent products featured in the design. From your Project page, you can click through to shopping results for each product and explore a wide range of visually similar options. This gives you flexibility to choose based on your own budget, retailer preferences, and style, making it easy to personalize your space.",
  },
  {
    question: "Do you offer professional design services?",
    answer:
      "Currently, Roomvia focuses on AI-powered design generation. However, our Business plan will soon include features for professional designers and real estate professionals.",
  },
  {
    question: "What's included in the different subscription plans?",
    answer:
      "Free accounts allow you to pay as you go, only buying the credit packs needed for your project(s). Monthly subscription accounts include 250 monthly credits, offering our best value for users with on-going design needs.",
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer:
      "Yes, you can cancel your subscription at any time from your account settings. You'll continue to have access to your plan features until the end of your current billing period. Unused credits from monthly will rollover and accumulate.",
  },
  {
    question: "How do I export or share my designs?",
    answer:
      "You can export your designs as high-resolution images by clicking the download icon found on all of your images. Simply magnify the image and the download option will appear.",
  },
  {
    question: "Is there a mobile app?",
    answer:
      "Roomvia is currently available as a web application that works great on mobile browsers. We're working on dedicated mobile apps for iOS and Android - stay tuned for updates!",
  },
  {
    question: "How do I get help if I'm stuck?",
    answer:
      "We're here to help! You can browse this FAQ section, contact us using the form below, or email us directly at support@roomvia.ai. Pro subscribers get priority email support with faster response times.",
  },
];

/*─────────────────────────── Component ───────────────────────────────────*/

export function SupportPage(): React.JSX.Element {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleInputChange = (field: string, value: string): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    };
    setIsSubmitting(true);

    try {
      const res = await fetch("/api/support/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      };
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        category: "",
        message: "",
      });
    } catch {
      /* eslint-disable no-alert */
      alert("Failed to send message. Please try again later.");
      /* eslint-enable no-alert */
    } finally {
      setIsSubmitting(false);
    };
  };

  const handleCopyEmail = (): void => {
    navigator.clipboard.writeText("support@roomvia.ai");
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const canSubmit =
    formData.name && formData.email && formData.subject && formData.message;

  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Support &amp; Help Center</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Find answers to common questions or get in touch with our support team
        </p>
      </div>

      {/* Support Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <Card>
          <CardHeader className="text-center">
            <Mail className="h-8 w-8 text-[#40A1AB] mx-auto mb-2" />
            <CardTitle>Email Support</CardTitle>
            <CardDescription>Send us a detailed message</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" onClick={handleCopyEmail} className="relative bg-transparent">
              {emailCopied ? "Copied!" : "support@roomvia.ai"}
              {emailCopied && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  ✓
                </span>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="text-center">
            <Clock className="h-8 w-8 text-[#40A1AB] mx-auto mb-2" />
            <CardTitle>Response Time</CardTitle>
            <CardDescription>We typically respond within</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="font-medium">24 hours</p>
            <p className="text-sm text-gray-500">Pro users: 4 hours</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* FAQ Section */}
        <div>
          <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Contact Form */}
        <div>
          <h2 className="text-3xl font-bold mb-6">Contact Us</h2>

          {isSubmitted ? (
            <Card>
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                <p className="text-gray-600 mb-4">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
                <Button variant="outline" onClick={() => setIsSubmitted(false)}>
                  Send Another Message
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => handleInputChange("category", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technical">Technical Issue</SelectItem>
                        <SelectItem value="billing">Billing &amp; Payments</SelectItem>
                        <SelectItem value="account">Account Management</SelectItem>
                        <SelectItem value="design">Design Questions</SelectItem>
                        <SelectItem value="feature">Feature Request</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => handleInputChange("subject", e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      className="min-h-[120px]"
                      placeholder="Please describe your question or issue in detail..."
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                    disabled={!canSubmit || isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/*──────────────────────────── End of file ────────────────────────────────*/
